import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { Submission } from '@guess/engine';
import type { AvatarId, ClientMessage, ColorId, ServerMessage } from '@guess/protocol';
import { initialState, reduce, type GameEvent, type GameState } from './state';

/**
 * La WebSocket, et rien d'autre.
 *
 * L'URL n'est pas configurable, et c'est voulu : le client se connecte a
 * l'origine d'ou il a ete charge, sur `/ws`. C'est la contrepartie du choix
 * de deploiement (meme origine pour la page et la socket) — il n'y a donc
 * aucune variable d'environnement a oublier, et aucun CORS a debattre.
 *
 * Le schema suit celui de la page : une page en `https` ouvre une socket en
 * `wss`. C'est exactement le piege decrit dans le README — une socket en clair
 * depuis une page chiffree echoue en silence — et le deduire plutot que de
 * l'ecrire en dur le rend impossible.
 */
function socketUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/ws`;
}

/** Un battement discret pour que les proxys ne coupent pas une partie calme. */
const PING_MS = 25_000;

// ---------------------------------------------------------------------------
// Session persistee
// ---------------------------------------------------------------------------
// Le SEUL moyen de reprendre un siege : le protocole `rejoin` exige l'id que
// le serveur nous a attribue, et rien d'autre ne le retient. Sans ceci, un
// simple rechargement de page en salon perd la place — pas seulement une
// vraie coupure reseau en pleine manche.

interface Session {
  room: string;
  playerId: string;
}

const SESSION_KEY = 'guess:session';

function readSession(): Session | null {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (typeof parsed.room === 'string' && typeof parsed.playerId === 'string') {
      return { room: parsed.room, playerId: parsed.playerId };
    }
  } catch {
    /* stockage refuse ou corrompu : on repartira d un join normal */
  }
  return null;
}

function writeSession(session: Session): void {
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* sans importance : juste pas de reprise automatique cette fois */
  }
}

function clearSession(): void {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    /* rien a faire */
  }
}

export interface GameActions {
  /** Un code vide cree un salon : c'est le bouton "nouvelle partie". */
  join(room: string, name: string): void;
  leave(): void;
  /** Retente la session enregistree, apres une connexion perdue. */
  retryConnection(): void;
  addBot(level: 'facile' | 'normal' | 'fort'): void;
  removeBot(playerId: string): void;
  setLoadout(powers: string[]): void;
  /** Reglages de partie, avant le lancement. Hote seulement. */
  configure(settings: { themes?: string[]; rounds?: number; powersEnabled?: boolean }): void;
  /** Choisi dans le salon. L'un ou l'autre, ou les deux a la fois. */
  customize(choice: { avatarId?: AvatarId; colorId?: ColorId }): void;
  setReady(ready: boolean): void;
  setStake(amount: number): void;
  usePower(powerId: string, targetId?: string): void;
  /** Demande l'exclusivite de reponse, sur une manche a buzzer. */
  buzz(): void;
  answer(submission: Submission): void;
  dismissError(): void;
}

export interface Connection {
  state: GameState;
  actions: GameActions;
}

export function useConnection(): Connection {
  const [state, dispatch] = useReducer(reduce, initialState);
  const socketRef = useRef<WebSocket | null>(null);
  /** Ce qu'on enverra des l'ouverture : la socket n'est pas prete tout de suite. */
  const pendingJoinRef = useRef<ClientMessage | null>(null);
  /**
   * Vrai entre l'envoi d'un `rejoin` et la reponse du serveur.
   *
   * Toute session enregistree meurt au premier redemarrage du serveur (rien
   * n'est persiste cote salon) — donc une reprise automatique au chargement
   * echoue tres facilement, et pas seulement en developpement : un simple
   * redeploiement suffit. Sans ce marqueur, l'erreur du serveur passait par
   * le circuit normal et laissait `status` coince a `connecting` pour de bon,
   * avec un accueil aux boutons desactives et aucun moyen de s'en sortir sans
   * vider le stockage a la main.
   */
  const awaitingRejoinRef = useRef(false);

  const send = useCallback((msg: ClientMessage) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
  }, []);

  const close = useCallback((event: GameEvent) => {
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket) {
      // On neutralise les rappels avant de fermer : une fermeture qu'on a
      // demandee ne doit pas repasser par la branche "connexion perdue".
      socket.onclose = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.close();
    }
    dispatch(event);
  }, []);

  /** Ouvre une socket et lui fait porter UN message initial, join ou rejoin. */
  const open = useCallback(
    (initial: ClientMessage) => {
      close({ type: 'connecting' });
      pendingJoinRef.current = initial;
      awaitingRejoinRef.current = initial.t === 'rejoin';

      const socket = new WebSocket(socketUrl());
      socketRef.current = socket;

      socket.onopen = () => {
        const pending = pendingJoinRef.current;
        pendingJoinRef.current = null;
        if (pending) socket.send(JSON.stringify(pending));
      };

      socket.onmessage = (event) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(String(event.data)) as ServerMessage;
        } catch {
          return; // un message illisible n'est pas une raison de tomber
        }
        // La session se retient au plus pres du reseau, avant meme que le
        // reducteur ne voie le message : c'est elle qui rend un `rejoin`
        // possible la prochaine fois, join comme reconnect y repassent.
        if (msg.t === 'joined') {
          awaitingRejoinRef.current = false;
          writeSession({ room: msg.room, playerId: msg.playerId });
        }

        // Le rejoin automatique a echoue (salon disparu, id perime) : la
        // session est morte, il faut l'oublier plutot que de rester coince
        // sur "Connexion..." avec un accueil aux boutons desactives.
        if (msg.t === 'error' && awaitingRejoinRef.current) {
          awaitingRejoinRef.current = false;
          clearSession();
          close({ type: 'reset' });
          return;
        }

        // L'instant de reception est capture ICI, au plus pres du reseau :
        // c'est lui qui transforme un "reste" serveur en echeance locale.
        dispatch({ type: 'server', msg, at: performance.now() });
      };

      socket.onclose = () => {
        socketRef.current = null;
        // Sans `reason` : la coupure est deja annoncee par la superposition
        // plein ecran, et le bandeau d'erreur dirait la meme chose deux fois.
        dispatch({ type: 'closed' });
      };

      socket.onerror = () => {
        // `onclose` suit toujours : inutile d'ecrire deux fois la meme erreur.
      };
    },
    [close],
  );

  const join = useCallback(
    (room: string, name: string) => {
      // Un join explicite est une nouvelle identite : la session d'une
      // eventuelle ancienne partie ne doit pas ressurgir au prochain montage.
      clearSession();
      open({ t: 'join', room: room.trim().toUpperCase(), name: name.trim() });
    },
    [open],
  );

  const retryConnection = useCallback(() => {
    const session = readSession();
    if (session) open({ t: 'rejoin', room: session.room, playerId: session.playerId });
  }, [open]);

  // Au tout premier montage — typiquement un rechargement de page — on tente
  // UNE reprise automatique s'il reste une session. Une seule tentative,
  // jamais de boucle : si elle echoue, la superposition de connexion perdue
  // prend le relais avec un bouton explicite plutot qu'un reessai silencieux.
  useEffect(() => {
    const session = readSession();
    if (session) retryConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Le battement. Il s'arrete tout seul quand la socket n'est plus ouverte.
  useEffect(() => {
    const timer = window.setInterval(() => send({ t: 'ping' }), PING_MS);
    return () => window.clearInterval(timer);
  }, [send]);

  // Au demontage seulement : on ne laisse pas une socket derriere nous.
  useEffect(() => {
    return () => {
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, []);

  const actions = useMemo<GameActions>(
    () => ({
      join,
      leave: () => {
        // Sans ce message, fermer la socket ne serait qu'une coupure : le
        // serveur garderait le siege pour un `rejoin` qui ne viendra pas.
        // `close()` de la WebSocket vide ses envois en attente avant de
        // fermer, le message part donc bien. Sans socket ouverte (connexion
        // deja perdue), `send` ne fait rien : le siege reste alors a table,
        // faute de canal pour le rendre.
        send({ t: 'leave' });
        // Un depart volontaire efface la session : au prochain montage, on
        // ne doit PAS ressusciter une partie qu'on vient de quitter expres.
        clearSession();
        close({ type: 'reset' });
      },
      retryConnection,
      addBot: (level) => send({ t: 'addBot', level }),
      removeBot: (playerId) => send({ t: 'removeBot', playerId }),
      setLoadout: (powers) => send({ t: 'loadout', powers }),
      configure: (settings) => send({ t: 'configure', ...settings }),
      customize: (choice) => send({ t: 'customize', ...choice }),
      setReady: (ready) => send({ t: 'ready', ready }),
      setStake: (amount) => send({ t: 'stake', amount }),
      usePower: (powerId, targetId) =>
        send(targetId === undefined ? { t: 'power', powerId } : { t: 'power', powerId, targetId }),
      buzz: () => send({ t: 'buzz' }),
      answer: (submission) => {
        send({ t: 'answer', submission });
        // On verrouille l'entree sans attendre la reponse du serveur : le
        // joueur a lache sa cartouche, le voir hesiter serait un mensonge.
        dispatch({ type: 'submitted' });
      },
      dismissError: () => dispatch({ type: 'dismissError' }),
    }),
    [join, close, retryConnection, send],
  );

  return { state, actions };
}
